import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import {
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { 
  isNativePlatform, 
  triggerHaptic,
  checkLocationPermissions,
  requestLocationPermissions,
  getLocationWithPermission
} from '../utils/capacitor';
import { executeRecaptcha } from '../utils/recaptcha';
import { sealLocationPayload, isLocationEncryptionConfigured } from '../utils/locationCrypto';
import { useAuth } from '../context/AuthContext';

const CheckInWidget = ({ refreshSignal = 0, deviceLock = null, onRequestUnlock = null }) => {
  const { user } = useAuth();
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [attendancePolicyError, setAttendancePolicyError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationPermission, setLocationPermission] = useState(null);
  const [todayHours, setTodayHours] = useState(null);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);

  useEffect(() => {
    checkStatus();
    fetchLocations();
    requestLocationPermission();
    fetchTodayHours();

    // Refresh status every 60 seconds if checked in
    const interval = setInterval(() => {
      if (checkInStatus?.checkedIn) {
        checkStatus();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [checkInStatus?.checkedIn, refreshSignal]);

  const fetchTodayHours = async () => {
    try {
      const response = await api.get('/mobile-attendance/today');
      if (response.data.totalHours) {
        setTodayHours(parseFloat(response.data.totalHours));
      }
    } catch (error) {
      if (error.response?.data?.code === 'WEB_ATTENDANCE_DISABLED') {
        setAttendancePolicyError(error.response.data);
        setTodayHours(null);
        return;
      }
      console.error('Error fetching today hours:', error);
    }
  };

  const requestLocationPermission = async () => {
    try {
      // Check current permission status
      const permission = await checkLocationPermissions();
      setLocationPermission(permission.location);
      
      // If not granted, request permissions
      if (permission.location !== 'granted') {
        const request = await requestLocationPermissions();
        setLocationPermission(request.location);
        
        if (request.location === 'granted') {
          console.log('✅ Location permission granted');
        } else {
          console.warn('⚠️ Location permission denied');
          toast.warning('Location permission is required for check-in/checkout');
        }
      } else {
        console.log('✅ Location permission already granted');
      }
    } catch (error) {
      console.error('Error requesting location permission:', error);
      setLocationPermission('denied');
      toast.error('Failed to request location permission');
    }
  };

  const getCurrentPosition = async () => {
    try {
      // Use the new getLocationWithPermission which handles permissions automatically
      const position = await getLocationWithPermission({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      });

      if (!position) {
        throw new Error('Location permission denied or unavailable');
      }

      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        timestamp: position.timestamp
      };
    } catch (error) {
      console.error('Error getting current position:', error);
      throw error;
    }
  };

  const getDeviceInfo = () => {
    return {
      platform: isNativePlatform() ? 'mobile' : 'web',
      userAgent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      deviceMemory: navigator.deviceMemory || null,
      hardwareConcurrency: navigator.hardwareConcurrency || null,
      maxTouchPoints: navigator.maxTouchPoints || 0
    };
  };

  // Encrypt + sign the GPS fix so the backend can distinguish an app request
  // from a hand-rolled one. Builds without keys configured fall back to the
  // plaintext shape, which the server accepts until
  // ATTENDANCE_REQUIRE_ENCRYPTED_LOCATION is turned on.
  const buildLocationBody = async (action, position) => {
    if (!isLocationEncryptionConfigured()) {
      console.warn('Location encryption is not configured in this build; sending plaintext coordinates');
      return {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        altitude: position.altitude,
        speed: position.speed,
        heading: position.heading,
        timestamp: new Date(position.timestamp).toISOString()
      };
    }

    const locationPayload = await sealLocationPayload({
      action,
      userId: user?.id,
      position
    });

    return { locationPayload };
  };

  const checkStatus = async () => {
    try {
      const response = await api.get('/mobile-attendance/status');
      if (response.data?.attendancePolicyError) {
        setAttendancePolicyError(response.data.attendancePolicyError);
      } else {
        setAttendancePolicyError(null);
      }
      setCheckInStatus(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.data?.code === 'WEB_ATTENDANCE_DISABLED') {
        setAttendancePolicyError(error.response.data);
        setCheckInStatus({ checkedIn: false });
        setLoading(false);
        return;
      }
      if (error.response?.status === 423 && error.response?.data?.deviceLock) {
        setCheckInStatus({
          checkedIn: false,
          deviceLock: error.response.data.deviceLock
        });
        setLoading(false);
        return;
      }
      console.error('Error checking status:', error);
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations/my-locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleCheckIn = async () => {
    if (locationPermission !== 'granted') {
      toast.error('Location permission is required for check-in');
      await requestLocationPermission();
      return;
    }

    setActionLoading(true);
    try {
      toast.info('Getting your location...');
      const position = await getCurrentPosition();

      const deviceInfo = getDeviceInfo();

      // Execute reCAPTCHA
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('check_in');
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError);
        toast.error('reCAPTCHA verification failed. Please refresh the page and try again.');
        setActionLoading(false);
        return;
      }

      const response = await api.post('/mobile-attendance/check-in', {
        ...(await buildLocationBody('check_in', position)),
        deviceInfo,
        recaptchaToken
      });

      await triggerHaptic('medium');
      toast.success(response.data.message);
      checkStatus();
      fetchTodayHours();
    } catch (error) {
      console.error('Error checking in:', error);
      const message = error.name === 'LocationSealError'
        ? error.message
        : (error.response?.data?.message || error.response?.data?.error || 'Failed to check in');
      const nearestLocation = error.response?.data?.nearestLocation;
      
      if (nearestLocation) {
        toast.error(
          `${message}\n\nNearest location: ${nearestLocation.name} (${nearestLocation.distance}m away)`,
          { autoClose: 5000 }
        );
      } else {
        toast.error(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (locationPermission !== 'granted') {
      toast.error('Location permission is required for check-out');
      await requestLocationPermission();
      return;
    }

    setActionLoading(true);
    try {
      toast.info('Getting your location...');
      const position = await getCurrentPosition();

      const deviceInfo = getDeviceInfo();

      // Execute reCAPTCHA
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('check_out');
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError);
        toast.error('reCAPTCHA verification failed. Please refresh the page and try again.');
        setActionLoading(false);
        return;
      }

      const response = await api.post('/mobile-attendance/check-out', {
        ...(await buildLocationBody('check_out', position)),
        deviceInfo,
        recaptchaToken
      });

      await triggerHaptic('heavy');
      
      const dailyHoursInfo = response.data.dailyHours;
      const sessionHours = response.data.workingHours;
      
      setShowCheckoutConfirm(false);
      
      toast.success(
        `${response.data.message}\n\n` +
        `This Session: ${sessionHours} hours\n` +
        (dailyHoursInfo ? `Today's Total: ${dailyHoursInfo.totalHours} hours` : ''),
        { autoClose: 6000 }
      );
      checkStatus();
      fetchTodayHours();
    } catch (error) {
      console.error('Error checking out:', error);
      const message = error.name === 'LocationSealError'
        ? error.message
        : (error.response?.data?.message || error.response?.data?.error || 'Failed to check out');
      const distance = error.response?.data?.distance;
      const requiredRadius = error.response?.data?.requiredRadius;
      const locationName = error.response?.data?.locationName;
      
      if (distance && requiredRadius && locationName) {
        toast.error(
          `Too far to check out!\n\nYou are ${distance}m away from ${locationName}.\nYou need to be within ${requiredRadius}m.`,
          { autoClose: 6000 }
        );
      } else {
        toast.error(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const formatDuration = (hours, minutes) => {
    // Handle negative values (timezone issues)
    const absHours = Math.abs(hours);
    const absMinutes = Math.abs(minutes);
    const sign = (hours < 0 || minutes < 0) ? '-' : '';
    
    if (absHours === 0 && absMinutes === 0) return '0m';
    if (absHours === 0) return `${sign}${absMinutes}m`;
    if (absMinutes === 0) return `${sign}${absHours}h`;
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const isCheckedIn = checkInStatus?.checkedIn || false;
  const effectiveDeviceLock = deviceLock || checkInStatus?.deviceLock || null;
  const hasUnclosedCheckIn = checkInStatus?.hasUnclosedCheckIn || false;
  const hasCheckoutRequest = checkInStatus?.hasCheckoutRequest || false;
  const isAttendanceBlockedOnWeb = attendancePolicyError?.code === 'WEB_ATTENDANCE_DISABLED';
  const locationName = checkInStatus?.checkedIn && checkInStatus.checkIn?.location 
    ? checkInStatus.checkIn.location.name 
    : locations[0]?.name || 'Office - Main Building';

  if (effectiveDeviceLock?.attendanceBlocked) {
    return (
      <motion.div
        className="bg-white rounded-2xl shadow-professional-lg p-6 border border-red-200 overflow-hidden relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-700 flex items-center gap-2 text-xl font-bold">
              <div className="flex items-center justify-center w-10 h-10 bg-red-500 rounded-lg">
                <ClockIcon className="w-6 h-6 text-white" />
              </div>
              Attendance Locked
            </h3>
          </div>

          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">Phone locked due to security reasons</p>
            <p className="mt-2 text-sm text-red-700">
              This mobile app is currently locked for attendance use by <strong>{effectiveDeviceLock.lockedEmail}</strong>.
            </p>
            {effectiveDeviceLock.lockExpiresAt && (
              <p className="mt-2 text-xs text-red-600">
                Lock expires at {moment(effectiveDeviceLock.lockExpiresAt).format('MMM D, YYYY h:mm A')}
              </p>
            )}
            {effectiveDeviceLock.pendingRequest ? (
              <p className="mt-3 inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-red-700">
                Unlock request pending
              </p>
            ) : (
              onRequestUnlock && (
                <button
                  type="button"
                  onClick={onRequestUnlock}
                  className="mt-4 inline-flex items-center rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  Request Device Unlock
                </button>
              )
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  if (isAttendanceBlockedOnWeb) {
    return (
      <motion.div
        className="bg-white rounded-2xl shadow-professional-lg p-6 border border-amber-200 overflow-hidden relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-700 flex items-center gap-2 text-xl font-bold">
              <div className="flex items-center justify-center w-10 h-10 bg-amber-500 rounded-lg">
                <ClockIcon className="w-6 h-6 text-white" />
              </div>
              Attendance
            </h3>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">Mobile app only</p>
            <p className="mt-2 text-sm text-amber-800">
              {attendancePolicyError?.message || 'Attendance is only available in the mobile app.'}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
    <motion.div
      className="bg-white rounded-2xl shadow-professional-lg p-6 border border-gray-200 overflow-hidden relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
    >
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full blur-3xl opacity-50" />
      
      <div className="relative">
        {/* Title Section */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-700 flex items-center gap-2 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-500 rounded-lg">
              <ClockIcon className="w-6 h-6 text-white" />
            </div>
            Attendance
          </h3>
          <AnimatePresence mode="wait">
            {isCheckedIn && (
              <motion.span
                className="flex items-center gap-1 text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
              >
                <CheckCircleIcon className="w-4 h-4" />
                Active
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Animated Building Icon */}
        <div className="flex justify-center mb-6">
          <AnimatePresence mode="wait">
            {!isCheckedIn ? (
              <motion.div
                key="building-inactive"
                className="relative"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  {/* Building Base */}
                  <motion.rect
                    x="30"
                    y="40"
                    width="60"
                    height="70"
                    fill="url(#building-gradient-inactive)"
                    rx="4"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 40, opacity: 1 }}
                    transition={{ duration: 0.6, type: 'spring', stiffness: 100 }}
                  />
                  
                  {/* Windows - Animated */}
                  {[0, 1, 2].map((row) => (
                    <g key={row}>
                      {[0, 1, 2].map((col) => (
                        <motion.rect
                          key={`${row}-${col}`}
                          x={42 + col * 14}
                          y={50 + row * 16}
                          width="8"
                          height="10"
                          fill="#94a3b8"
                          rx="1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: [0.3, 0.5, 0.3] }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: (row * 3 + col) * 0.2
                          }}
                        />
                      ))}
                    </g>
                  ))}
                  {/* Door */}
                  <motion.rect
                    x="52"
                    y="90"
                    width="16"
                    height="20"
                    fill="#475569"
                    rx="2"
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    style={{ originY: 1 }}
                  />
                  {/* Roof */}
                  <motion.path
                    d="M25 40 L60 20 L95 40 Z"
                    fill="url(#roof-gradient)"
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  />
                  <defs>
                    <linearGradient id="building-gradient-inactive" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#cbd5e1" />
                      <stop offset="100%" stopColor="#94a3b8" />
                    </linearGradient>
                    <linearGradient id="roof-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#64748b" />
                      <stop offset="100%" stopColor="#475569" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>
            ) : (
              <motion.div
                key="building-active"
                className="relative"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  {/* Building Base - Active */}
                  <motion.rect
                    x="30"
                    y="40"
                    width="60"
                    height="70"
                    fill="url(#building-gradient-active)"
                    rx="4"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 40, opacity: 1 }}
                    transition={{ duration: 0.6, type: 'spring', stiffness: 100 }}
                  />
                  
                  {/* Windows - Active and Glowing */}
                  {[0, 1, 2].map((row) => (
                    <g key={row}>
                      {[0, 1, 2].map((col) => (
                        <motion.rect
                          key={`${row}-${col}`}
                          x={42 + col * 14}
                          y={50 + row * 16}
                          width="8"
                          height="10"
                          fill="#fbbf24"
                          rx="1"
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: [0.6, 1, 0.6],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: (row * 3 + col) * 0.15
                          }}
                        />
                      ))}
                    </g>
                  ))}
                  {/* Door - Active */}
                  <motion.rect
                    x="52"
                    y="90"
                    width="16"
                    height="20"
                    fill="#10b981"
                    rx="2"
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    style={{ originY: 1 }}
                  />
                  {/* Roof - Active */}
                  <motion.path
                    d="M25 40 L60 20 L95 40 Z"
                    fill="url(#roof-gradient-active)"
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  />
                  {/* Glow Effect */}
                  <motion.circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ 
                      scale: [0.8, 1.2, 0.8],
                      opacity: [0.3, 0, 0.3]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  />
                  <defs>
                    <linearGradient id="building-gradient-active" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#2563eb" />
                    </linearGradient>
                    <linearGradient id="roof-gradient-active" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#1e40af" />
                      <stop offset="100%" stopColor="#1e3a8a" />
                    </linearGradient>
                  </defs>
                </svg>
                {/* Success Checkmark Animation */}
                <motion.div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 10, delay: 0.4 }}
                >
                  <div className="bg-green-500 rounded-full p-3 shadow-lg">
                    <CheckCircleIcon className="w-8 h-8 text-white" />
            </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
      </div>

        <AnimatePresence mode="wait">
          {isCheckedIn ? (
            <motion.div
              key="checked-in"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Status Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                <p className="text-sm text-gray-600 mb-1">Checked In At</p>
                {checkInStatus?.checkIn && (
                  <p className="text-green-600 font-semibold">
                    {moment(checkInStatus.checkIn.checkInTime).tz('Asia/Kolkata').format('h:mm A')}
                  </p>
                )}
                {checkInStatus?.duration && (
                  <p className="text-sm text-gray-600 mt-1">
                    Duration: {formatDuration(checkInStatus.duration.hours, checkInStatus.duration.minutes)}
                  </p>
                )}
              </div>

              {/* Location Section */}
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <MapPinIcon className="w-4 h-4 mt-0.5 text-blue-600" />
                <span>{locationName}</span>
            </div>
            
              {/* Check Out Button */}
              <motion.button
                onClick={() => setShowCheckoutConfirm(true)}
                disabled={actionLoading}
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white py-3 rounded-xl flex items-center justify-center gap-2 shadow-professional-lg font-semibold"
                whileHover={{ scale: 1.02, boxShadow: '0 20px 25px -5px rgba(239, 68, 68, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <ArrowRightEndOnRectangleIcon className="w-5 h-5" />
                Check Out Now
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              key="checked-out"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Status Section */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p className="text-gray-500 font-semibold">Not Checked In</p>
            </div>

              {/* Unclosed Check-in Warning */}
              {hasUnclosedCheckIn && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border-l-4 p-4 rounded-r-lg ${
                    hasCheckoutRequest 
                      ? 'bg-blue-50 border-blue-400' 
                      : 'bg-yellow-50 border-yellow-400'
                  }`}
                >
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 mr-2 flex-shrink-0 ${
                      hasCheckoutRequest ? 'text-blue-600' : 'text-yellow-600'
                    }`} />
                    <div className="flex-1">
                      <p className={`text-sm font-semibold mb-1 ${
                        hasCheckoutRequest ? 'text-blue-800' : 'text-yellow-800'
                      }`}>
                        {hasCheckoutRequest ? 'Checkout Request Submitted' : 'Unclosed Check-in'}
                      </p>
                      <p className={`text-sm ${
                        hasCheckoutRequest ? 'text-blue-700' : 'text-yellow-700'
                      }`}>
                        {checkInStatus?.note || `You have an unclosed check-in from ${checkInStatus?.unclosedCheckInDate}. ${hasCheckoutRequest ? 'You have submitted a checkout request and can check in now.' : 'Please use "Request Checkout Time" to close it before checking in again.'}`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Location Section */}
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <MapPinIcon className="w-4 h-4 mt-0.5 text-blue-600" />
                <span>{locationName}</span>
            </div>

              {/* Check In Button */}
              <motion.button
                onClick={handleCheckIn}
                disabled={actionLoading || locationPermission !== 'granted' || (hasUnclosedCheckIn && !hasCheckoutRequest)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl flex items-center justify-center gap-2 shadow-professional-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: (hasUnclosedCheckIn && !hasCheckoutRequest) ? 1 : 1.02, boxShadow: (hasUnclosedCheckIn && !hasCheckoutRequest) ? 'none' : '0 20px 25px -5px rgba(59, 130, 246, 0.4)' }}
                whileTap={{ scale: (hasUnclosedCheckIn && !hasCheckoutRequest) ? 1 : 0.98 }}
            >
              {actionLoading ? (
                <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Getting Location...
                </>
              ) : (
                <>
                    <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
                    {(hasUnclosedCheckIn && !hasCheckoutRequest) ? 'Check In Disabled (Unclosed Check-in)' : 'Check In Now'}
                </>
              )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>

    {/* Checkout Confirmation Modal - Rendered via Portal to body */}
    {showCheckoutConfirm && typeof document !== 'undefined' && createPortal(
      <div className="fixed z-[9999] inset-0 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div 
            className="fixed inset-0 bg-gray-900/70 backdrop-blur-md transition-opacity" 
            onClick={() => setShowCheckoutConfirm(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          ></div>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

          <div className="relative inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-professional-lg transform transition-all duration-300 sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                  <ArrowRightEndOnRectangleIcon className="h-7 w-7 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-white">
                    Confirm Checkout
                  </h3>
                  <p className="text-sm text-red-100 mt-0.5">
                    Are you sure you want to check out?
                  </p>
                </div>
              </div>
          </div>

            {/* Content */}
            <div className="bg-white px-6 py-6">
              {checkInStatus?.checkIn && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Check-in Details:</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <ClockIcon className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-gray-700">
                          Checked in at: {moment(checkInStatus.checkIn.checkInTime).tz('Asia/Kolkata').format('h:mm A')}
                        </span>
                      </div>
                      {checkInStatus.duration && (
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-700">
                            Duration: {formatDuration(checkInStatus.duration.hours, checkInStatus.duration.minutes)}
                          </span>
                        </div>
                      )}
                      {checkInStatus.checkIn.location && (
                        <div className="flex items-center">
                          <MapPinIcon className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-700">
                            Location: {checkInStatus.checkIn.location.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <p className="text-sm text-blue-800">
                      Make sure you're at the office location before checking out.
                    </p>
          </div>
        </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200">
            <button
                type="button"
                onClick={handleCheckOut}
                disabled={actionLoading}
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-xl border border-transparent shadow-professional px-7 py-3.5 bg-gradient-to-r from-red-500 to-red-600 text-base font-semibold text-white hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
            >
              {actionLoading ? (
                <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                </>
              ) : (
                  'Confirm Checkout'
              )}
            </button>
              <button
                type="button"
                onClick={() => setShowCheckoutConfirm(false)}
                disabled={actionLoading}
                className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-xl border-2 border-gray-300 shadow-professional px-7 py-3.5 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
              >
                Cancel
              </button>
            </div>
            </div>
        </div>
      </div>,
      document.body
      )}
    </>
  );
};

export default CheckInWidget;
